import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { formatPhoneCanonical } from '../utils/phone';
import { useBrandProfile } from '../contexts/brand-profile';
import {
  BRAND_PROFILES,
  SIGNUP_BRAND_ORDER,
  brandProfileOf,
} from '../config/brandProfiles';
import Img from './ui/Img';
import {
  ADDRESS_MAX_LENGTH,
  POSTAL_CODE_ERROR,
  POSTAL_CODE_INPUT_MAX_LENGTH,
  normalizeAddress,
  parsePostalCode,
} from '../utils/address';

interface ParentData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  postal_code: string;
}


export function ParentOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signOut, user } = useAuth();
  const { activeBrand, setActiveBrand, availableBrands, hasChosenBrand, isMultiBrand } = useBrandProfile();

  /**
   * The brand this page is presenting — the header, the welcome copy and the picked card.
   *
   * A brand-new family starts on the brand most of them are here for. Only when they have not
   * already decided: a parent who arrived on a `?brand=` link, or who picked once and came
   * back, keeps their choice. Onboarding runs before the parent row exists, so there are no
   * enrollments to derive from either — this is the only signal there is, and leaving it at
   * the global default would greet most new families as the wrong brand.
   *
   * Derived rather than written on mount. Setting it in an effect would persist a preference
   * the parent has not expressed yet, and would fire a context update on every render of this
   * page. It is persisted at the one moment it becomes a real answer: when they submit.
   */
  // Sign-up order, not `BRAND_KEYS` order — intersected with what is actually on offer, which
  // is also what makes this inert when the multi-brand portal is off: `availableBrands` is
  // then the default brand alone, so the picker has one entry and hides itself.
  const pickerBrands = SIGNUP_BRAND_ORDER.filter(key => availableBrands.includes(key));
  const selectedBrand = hasChosenBrand ? activeBrand : pickerBrands[0] ?? activeBrand;
  const brandSelection = brandProfileOf(selectedBrand);
  /**
   * Whether this sign-up asks for a home address: only when the brand on screen has a reason
   * to (a resident discount). A family who switches the picker to BHCS stops being asked, and
   * whatever they had typed is not sent, because they were never asked on the brand they
   * signed up under. BHCS has no reason, so with the multi-brand portal off nobody is asked.
   */
  const addressPrompt = brandSelection.intake.addressPrompt;
  const showAddress = addressPrompt !== null;
  const [parentData, setParentData] = useState<ParentData>({
    first_name: user?.user_metadata?.full_name?.split(' ')[0] || '',
    last_name: user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: formatPhoneCanonical(user?.user_metadata?.phone_number || ''),
    address: '',
    postal_code: '',
  });
  const [phoneError, setPhoneError] = useState<string>('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState<string>('');

  /** Optional, so blank passes. Only a ZIP the database would reject is an error. */
  const validatePostalCode = (value: string) => {
    const ok = parsePostalCode(value).ok;
    setPostalCodeError(ok ? '' : POSTAL_CODE_ERROR);
    return ok;
  };

  const validatePhone = (phone: string) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    if (!digitsOnly) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (digitsOnly.length !== 10) {
      setPhoneError('Please enter a valid 10-digit US phone number');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const { mutate: createParent, isPending } = useMutation({
    mutationFn: async () => {
      if (!validatePhone(parentData.phone)) {
        throw new Error('Invalid phone number');
      }

      // Get the current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Not authenticated');
      }

      // Optional columns go in only when they have a value, so a BHCS sign-up (and every sign-up
      // while the multi-brand portal is off) inserts exactly what it did before they existed.
      const optional: { address?: string; postal_code?: string; brand_preference?: string } = {};
      if (showAddress) {
        const address = normalizeAddress(parentData.address);
        const postal = parsePostalCode(parentData.postal_code);
        if (!postal.ok) throw new Error(POSTAL_CODE_ERROR);
        if (address) optional.address = address;
        if (postal.value) optional.postal_code = postal.value;
      }
      // The brand this page was presenting, picked or not. The picker's clicks could not save it
      // to the account because the row did not exist yet, so the account gets it here.
      if (isMultiBrand) optional.brand_preference = selectedBrand;

      // Create the parent
      const { data: parent, error: parentError } = await supabase
        .from('parents')
        .insert([{
          first_name: parentData.first_name,
          last_name: parentData.last_name,
          email: session.user.email,
          phone: formatPhoneCanonical(parentData.phone),
          ...optional,
        }])
        .select()
        .single();

      if (parentError) {
        throw new Error(parentError.message);
      }

      return parent;
    },
    onSuccess: async () => {
      // Commit the brand the page was presenting. Untouched picker or not, this is the answer
      // they just signed up under, so the dashboard opens on the catalog they were shown.
      if (!hasChosenBrand) setActiveBrand(selectedBrand, 'signup');
      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ['parents'] });
      // The auth state will update automatically through Supabase's onAuthStateChange
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      if (error.message === 'Not authenticated') {
        navigate('/login');
      } else {
        window.alert(error.message);
      }
    }
  });

  const handleParentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParentData({
      ...parentData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, parentheses, spaces, and dashes
    let value = e.target.value.replace(/[^\d()-\s]/g, '');
    // Auto-format as (555) 555-5555
    value = value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d{0,3})(\d{0,4})/, (_, p1, p2, p3) => {
        let out = '';
        if (p1) out += `(${p1}`;
        if (p2) out += `) ${p2}`;
        if (p3) out += `-${p3}`;
        return out.trim();
      })
      .slice(0, 14);
    setParentData({
      ...parentData,
      phone: value,
    });
    if (phoneTouched && value) {
      validatePhone(value);
    } else if (!value) {
      setPhoneError('');
    }
  };

  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    validatePhone(parentData.phone);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneTouched(true);
    const phoneOk = validatePhone(parentData.phone);
    const postalOk = !showAddress || validatePostalCode(parentData.postal_code);
    if (phoneOk && postalOk) {
      createParent();
    }
  };

  const handleGoToLogin = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {brandSelection.logoPath && (
                <Img src={brandSelection.logoPath} alt="" className="h-8 w-auto" width={512} height={512} />
              )}
              <span className="ml-2 text-xl font-bold text-gray-900">{brandSelection.shortName}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleGoToLogin}
            >
              Back to Login
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <span className="text-4xl">🎉</span>
              </div>
              <CardTitle className="text-2xl">{brandSelection.intake.welcomeTitle}</CardTitle>
              <CardDescription className="text-base mt-2">
                {brandSelection.intake.welcomeBlurb}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Which brand they are here for. A brand-new family has no enrollments, so
                  nothing can infer this -- without the question their only way into the
                  Kaleido catalog is a `?brand=` link. The choice is persisted per account by
                  `setActiveBrand`, so finishing setup lands them in the right catalog.
                  Hidden entirely when there is only one brand to choose. */}
              {availableBrands.length > 1 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">✨</span>
                    <h3 className="text-lg font-medium">Which programs are you here for?</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {pickerBrands.map((key) => {
                      const option = BRAND_PROFILES[key];
                      const isActive = key === selectedBrand;
                      return (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setActiveBrand(key, 'signup')}
                          className={`flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
                            isActive ? '' : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                          style={
                            isActive
                              ? { borderColor: option.accent, backgroundColor: option.accentSoft }
                              : undefined
                          }
                        >
                          {option.logoPath && (
                            <Img
                              src={option.logoPath}
                              alt=""
                              className="h-8 w-8 flex-shrink-0 object-contain"
                              width={512}
                              height={512}
                            />
                          )}
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-gray-900">
                              {option.shortName}
                            </span>
                            <span className="block text-xs text-gray-500">{option.tagline}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    You can switch between them any time — your children and balance are shared.
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">👨‍👩‍👧‍👦</span>
                  <h3 className="text-lg font-medium">Tell us about yourself</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={parentData.first_name}
                      onChange={handleParentChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={parentData.last_name}
                      onChange={handleParentChange}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={parentData.email}
                    onChange={handleParentChange}
                    required
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={parentData.phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder="(555) 555-5555"
                    maxLength={14}
                    className="w-full rounded-md border border-input bg-background px-4 py-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    required
                  />
                  {phoneError && phoneTouched && (
                    <p className="text-sm text-red-500 mt-1">{phoneError}</p>
                  )}
                </div>
              </div>

              {showAddress && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🏠</span>
                    <h3 className="text-lg font-medium">
                      Home address <span className="text-sm font-normal text-gray-500">(optional)</span>
                    </h3>
                  </div>
                  {/* The reason comes before the fields: an unexplained request for a home
                      address is the one a parent skips. */}
                  <p
                    className="flex items-start gap-2 rounded-md px-3 py-2 text-sm"
                    style={{ backgroundColor: brandSelection.accentSoft, color: brandSelection.accentInk }}
                    data-testid="address-prompt"
                  >
                    <span aria-hidden="true">🏷️</span>
                    <span>{addressPrompt}</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="address">Street address</Label>
                      <Input
                        id="address"
                        name="address"
                        autoComplete="street-address"
                        value={parentData.address}
                        onChange={handleParentChange}
                        maxLength={ADDRESS_MAX_LENGTH}
                        placeholder="123 Main St, Foster City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">ZIP code</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        autoComplete="postal-code"
                        inputMode="numeric"
                        value={parentData.postal_code}
                        onChange={(e) => {
                          handleParentChange(e);
                          if (postalCodeError) validatePostalCode(e.target.value);
                        }}
                        onBlur={() => validatePostalCode(parentData.postal_code)}
                        maxLength={POSTAL_CODE_INPUT_MAX_LENGTH}
                        placeholder="94404"
                      />
                      {postalCodeError && (
                        <p className="text-sm text-red-500 mt-1">{postalCodeError}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    You can add or change this later on your Account page.
                  </p>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={isPending}
                  className="w-full sm:w-auto px-8 py-2 text-base font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm rounded-md transition-colors"
                >
                  {isPending ? 'Creating Account...' : 'Let\'s Get Started! 🚀'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
} 